import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Truck, ShieldCheck, ShieldX, Minus, Plus, GitCompareArrows, Check, ChevronRight, Heart, BadgeCheck, ImagePlus, X } from "lucide-react";
import { api, rupee, optimg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCompare } from "@/context/CompareContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

function Stars({ n, size = "w-4 h-4" }) {
  return <span className="inline-flex gap-0.5">{[1,2,3,4,5].map((s) => <Star key={s} className={`${size} ${s <= Math.round(n) ? "fill-terra text-terra" : "text-line"}`} />)}</span>;
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { ids, toggle } = useCompare();
  const { has, toggle: toggleWish } = useWishlist();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [rev, setRev] = useState({ average: 0, count: 0, breakdown: {}, reviews: [], has_more: false });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("recent");
  const [similar, setSimilar] = useState([]);
  const [eligOrder, setEligOrder] = useState(null);
  const [myReviewId, setMyReviewId] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [pin, setPin] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);
  const fileRef = useRef(null);

  const loadReviews = useCallback((p = 1, s = sort) => {
    api.get(`/products/${id}/reviews`, { params: { page: p, size: 5, sort: s } }).then(({ data }) => {
      setRev((prev) => p === 1 ? data : { ...data, reviews: [...prev.reviews, ...data.reviews] });
    });
  }, [id, sort]);

  useEffect(() => {
    setActive(0); setQty(1); setPage(1);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      api.get("/products", { params: { category: data.category, limit: 6 } })
        .then((r) => setSimilar(r.data.filter((p) => p.id !== data.id).slice(0, 4)));
    });
    loadReviews(1, sort);
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!user) { setEligOrder(null); setMyReviewId(null); return; }
    api.get("/orders").then(({ data }) => {
      const o = data.find((o) => o.status === "Delivered" && o.items.some((it) => it.product_id === id));
      setEligOrder(o || null);
      if (o) api.get("/my-reviews").then(({ data: mine }) => setMyReviewId(mine[`${o.id}_${id}`] || null));
    });
  }, [user, id]);

  if (!product) return <div className="min-h-screen grid place-items-center text-mute">Loading…</div>;
  const price = product.discount ? Math.round(product.price * (1 - product.discount / 100)) : product.price;
  const inCompare = ids.includes(product.id);

  const checkShipping = async () => {
    if (!/^\d{6}$/.test(pin)) { setEstimate(null); toast.error("Enter a valid 6-digit pincode"); return; }
    setEstLoading(true);
    try {
      const { data } = await api.get(`/products/${product.id}/shipping-estimate`, { params: { pincode: pin } });
      setEstimate(data);
    } catch { setEstimate({ ok: false }); } finally { setEstLoading(false); }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    for (const f of files) {
      try { const { data } = await api.post("/upload", (() => { const fd = new FormData(); fd.append("file", f); return fd; })(), { headers: { "Content-Type": "multipart/form-data" } }); if (data.kind === "image") setPhotos((p) => [...p, abs(data.url)]); } catch { toast.error("Upload failed"); }
    }
  };

  const submitReview = async () => {
    try {
      if (myReviewId) await api.put(`/reviews/${myReviewId}`, { rating, comment, images: photos });
      else await api.post("/reviews", { product_id: id, order_id: eligOrder.id, rating, comment, images: photos });
      toast.success("Thanks for your review!");
      setComment(""); setPhotos([]);
      setPage(1); loadReviews(1, sort);
      api.get(`/products/${id}`).then(({ data }) => setProduct(data));
      api.get("/my-reviews").then(({ data: mine }) => setMyReviewId(mine[`${eligOrder.id}_${id}`] || null));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit review"); }
  };

  const changeSort = (s) => { setSort(s); setPage(1); api.get(`/products/${id}/reviews`, { params: { page: 1, size: 5, sort: s } }).then(({ data }) => setRev(data)); };

  return (
    <div className="pt-20 sm:pt-24 pb-24">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-1.5 text-xs text-mute py-5">
          <Link to="/shop" className="hover:text-clay">Shop</Link><ChevronRight className="w-3 h-3" />
          <Link to={`/shop?category=${product.category}`} className="hover:text-clay">{product.category}</Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <motion.div layout className="relative aspect-[4/5] rounded-sm overflow-hidden bg-surface">
              <motion.img key={active} src={optimg(product.images[active], 900, 72)} alt={product.title}
                initial={{ opacity: 0.4, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="w-full h-full object-cover" />
            </motion.div>
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-3">
                {product.images.map((im, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-20 h-24 rounded-sm overflow-hidden border-2 ${active === i ? "border-clay" : "border-transparent opacity-70"}`} data-testid={`thumb-${i}`}>
                    <img src={optimg(im, 160, 60)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {product.video_url && (
              <div className="mt-4"><div className="overline text-mute mb-2">Watch it made</div>
                <video src={product.video_url} controls playsInline className="w-full rounded-sm bg-ink" data-testid="product-video" /></div>
            )}
          </div>

          {/* Info */}
          <div className="lg:pt-4">
            <h1 className="font-serif text-3xl sm:text-5xl tracking-tight leading-tight">{product.title}</h1>
            {/* price block */}
            <div className="flex items-baseline gap-3 mt-4">
              <span className="text-4xl font-semibold text-clay" data-testid="product-price">{rupee(price)}</span>
              {product.discount > 0 && <span className="text-xl text-mute line-through">{rupee(product.price)}</span>}
            </div>
            {/* compact rating line */}
            <div className="flex items-center gap-2 mt-2 text-sm text-mute" data-testid="rating-line">
              {rev.count > 0 ? <><Stars n={rev.average} /> <span>{rev.average} · {rev.count} review{rev.count > 1 ? "s" : ""}</span></> : <span>No reviews yet</span>}
            </div>

            <p className="text-mute leading-relaxed mt-6">{product.description}</p>

            {/* Purchase block */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center border border-line rounded-full w-fit">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 min-h-[44px]" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 min-h-[44px]" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={async () => { const ok = await add(product.id, qty); if (!ok) nav("/login"); }}
                className="w-full rounded-full bg-clay text-sand py-4 text-sm hover:bg-forest transition-colors min-h-[48px]" data-testid="add-to-cart-btn">
                Add to Basket
              </button>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleWish(product.id)} className={`w-12 h-12 grid place-items-center rounded-full border transition-colors ${has(product.id) ? "bg-clay text-sand border-clay" : "border-line hover:border-clay"}`} data-testid="detail-wishlist-btn" aria-label="Save"><Heart className={`w-5 h-5 ${has(product.id) ? "fill-sand" : ""}`} /></button>
                <button onClick={() => toggle(product.id)} className={`w-12 h-12 grid place-items-center rounded-full border transition-colors ${inCompare ? "bg-forest text-sand border-forest" : "border-line hover:border-clay"}`} data-testid="detail-compare-btn" aria-label="Compare">{inCompare ? <Check className="w-5 h-5" /> : <GitCompareArrows className="w-5 h-5" />}</button>
              </div>
            </div>

            {/* seller + delivery + refund */}
            <div className="mt-6 space-y-2 text-sm border-t border-line pt-5">
              <div>Sold by <Link to={`/seller/${product.seller_id}`} className="text-clay font-medium link-underline" data-testid="seller-name-link">{product.seller_name}</Link></div>
              <div className="flex items-center gap-2 text-mute"><Truck className="w-4 h-4 text-clay" /> Delivers in <b className="text-ink">{product.delivery_days} days</b></div>
              <div className="flex items-center gap-2 text-mute">{product.refundable ? <ShieldCheck className="w-4 h-4 text-forest" /> : <ShieldX className="w-4 h-4 text-clay" />} {product.refundable ? "Refundable" : "Non-refundable"}</div>

              {/* shipping estimator */}
              <div className="pt-2">
                <div className="flex items-center gap-2">
                  <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyDown={(e) => e.key === "Enter" && checkShipping()} placeholder="Enter pincode"
                    className="w-36 bg-surface border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-estimate-pincode" inputMode="numeric" />
                  <button onClick={checkShipping} disabled={estLoading}
                    className="rounded-full border border-clay text-clay px-4 py-2 text-sm hover:bg-clay hover:text-sand transition-colors disabled:opacity-60" data-testid="ship-estimate-btn">
                    {estLoading ? "…" : "Check shipping"}
                  </button>
                </div>
                {estimate && (
                  <div className="mt-2 text-sm" data-testid="ship-estimate-result">
                    {!estimate.ok ? (
                      <span className="text-clay">Couldn't detect that pincode — shipping shown at checkout.</span>
                    ) : estimate.free ? (
                      <span className="text-forest font-medium">Free shipping to {estimate.state} ✦ <span className="text-mute font-normal">estimated</span></span>
                    ) : (
                      <span className="text-ink">Shipping <b>{rupee(estimate.shipping)}</b> to {estimate.state} <span className="text-mute">· estimated, final at checkout</span></span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <Reveal>
          <div className="mt-24" data-testid="reviews-section">
            <div className="overline text-clay mb-3">Verified reviews</div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-10">
              {/* summary */}
              <div>
                {rev.count === 0 ? (
                  <p className="text-mute" data-testid="no-reviews">No reviews yet.</p>
                ) : (
                  <div className="bg-surface border border-line rounded-sm p-6">
                    <div className="font-serif text-5xl">{rev.average}</div>
                    <Stars n={rev.average} size="w-5 h-5" />
                    <div className="text-sm text-mute mt-1">{rev.count} review{rev.count > 1 ? "s" : ""}</div>
                    <div className="mt-4 space-y-1.5">
                      {[5,4,3,2,1].map((s) => { const c = rev.breakdown[s] || 0; const pct = rev.count ? (c / rev.count) * 100 : 0; return (
                        <div key={s} className="flex items-center gap-2 text-xs">
                          <span className="w-6 text-mute">{s}★</span>
                          <div className="flex-1 h-2 bg-line rounded-full overflow-hidden"><div className="h-full bg-terra" style={{ width: `${pct}%` }} /></div>
                          <span className="w-6 text-right text-mute">{c}</span>
                        </div>); })}
                    </div>
                  </div>
                )}
                {/* review form */}
                {user && eligOrder && (
                  <div className="mt-5 bg-sand border border-line rounded-sm p-5" data-testid="review-form">
                    <div className="text-sm font-medium mb-2">{myReviewId ? "Edit your review" : "Write a review"}</div>
                    <div className="flex gap-1 mb-3">{[1,2,3,4,5].map((s) => <button key={s} onClick={() => setRating(s)} data-testid={`star-${s}`}><Star className={`w-6 h-6 ${s <= rating ? "fill-terra text-terra" : "text-line"}`} /></button>)}</div>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="w-full bg-surface border border-line rounded-sm p-3 text-sm outline-none focus:ring-2 focus:ring-clay resize-none" rows={3} data-testid="review-comment" />
                    <div className="flex items-center gap-2 mt-2">
                      {photos.map((ph, i) => <div key={i} className="relative w-12 h-12 rounded overflow-hidden"><img src={ph} alt="" className="w-full h-full object-cover" /><button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute top-0 right-0 bg-ink/70 text-sand rounded-bl"><X className="w-3 h-3" /></button></div>)}
                      {photos.length < 3 && <button onClick={() => fileRef.current?.click()} className="w-12 h-12 border-2 border-dashed border-line rounded grid place-items-center text-mute" data-testid="review-photo-btn"><ImagePlus className="w-4 h-4" /></button>}
                      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadPhoto} />
                    </div>
                    <button onClick={submitReview} className="mt-3 rounded-full bg-clay text-sand px-6 py-2.5 text-sm hover:bg-forest transition-colors" data-testid="submit-review-btn">{myReviewId ? "Update" : "Post review"}</button>
                  </div>
                )}
                {user && !eligOrder && <p className="text-xs text-mute mt-4">Only buyers with a delivered order can review.</p>}
                {!user && <Link to="/login" className="inline-block mt-4 text-sm text-clay link-underline">Sign in to review</Link>}
              </div>

              {/* list */}
              <div>
                {rev.count > 0 && (
                  <div className="flex gap-2 mb-5">
                    {[["recent","Most recent"],["high","Highest"],["low","Lowest"]].map(([v,l]) => (
                      <button key={v} onClick={() => changeSort(v)} className={`text-xs px-3 py-1.5 rounded-full border ${sort===v?"bg-clay text-sand border-clay":"border-line text-mute"}`} data-testid={`review-sort-${v}`}>{l}</button>
                    ))}
                  </div>
                )}
                <div className="space-y-6">
                  {rev.reviews.map((r) => (
                    <div key={r.id} className="border-b border-line pb-5" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium flex items-center gap-2">{r.buyer_name}{r.verified && <span className="inline-flex items-center gap-1 text-[10px] text-forest bg-forest/10 px-2 py-0.5 rounded-full"><BadgeCheck className="w-3 h-3" /> Verified Buyer</span>}</span>
                        <span className="text-xs text-mute">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                      </div>
                      <Stars n={r.rating} />
                      <p className="text-mute text-sm mt-2 leading-relaxed">{r.comment}</p>
                      {r.images?.length > 0 && <div className="flex gap-2 mt-2">{r.images.map((im, i) => <img key={i} src={im} alt="" className="w-16 h-16 rounded object-cover" />)}</div>}
                      {r.seller_reply && <div className="mt-3 ml-4 bg-surface border-l-2 border-clay pl-3 py-2 rounded-sm"><span className="overline text-clay">Seller response</span><p className="text-sm mt-1">{r.seller_reply}</p></div>}
                    </div>
                  ))}
                </div>
                {rev.has_more && <button onClick={() => { const np = page + 1; setPage(np); loadReviews(np, sort); }} className="mt-6 rounded-full border border-clay text-clay px-6 py-2.5 text-sm hover:bg-clay hover:text-sand transition-colors" data-testid="load-more-reviews">Load more</button>}
              </div>
            </div>
          </div>
        </Reveal>

        {similar.length > 0 && (
          <div className="mt-24">
            <div className="overline text-clay mb-3">Similar handmade finds</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">You may also love</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {similar.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
